(() => {
  'use strict';
  const historyKey = 'recentlyViewedProduct';
  const text = value => String(value ?? '').trim();
  const key = value => text(value).toLocaleLowerCase().replace(/\s+/g, ' ');
  const node = (tag, className, content) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (content !== undefined) element.textContent = content;
    return element;
  };
  const productURL = value => {
    try {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || url.protocol !== 'https:' || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9][a-z0-9-]*\/?$/i.test(url.pathname)) return null;
      return url;
    } catch (_) { return null; }
  };
  const imageURL = value => {
    if (!text(value)) return null;
    try {
      const url = new URL(value, location.origin);
      if (url.protocol !== 'https:') return null;
      url.searchParams.set('width', '480');
      return url;
    } catch (_) { return null; }
  };
  function normalize(value) {
    if (!value || !value.id || !text(value.title)) return null;
    const url = productURL(value.url || (value.handle ? `${window.Shopify?.routes?.root || '/'}products/${value.handle}` : ''));
    const image = imageURL(value.featured_image?.url || value.featured_image || value.images?.[0] || value.image);
    const min = Number(value.price_min ?? value.price), max = Number(value.price_max ?? value.price);
    if (!url || !Number.isFinite(min) || min < 0 || !Number.isFinite(max) || max < min) return null;
    const options = [];
    (Array.isArray(value.options) ? value.options : []).slice(0, 3).forEach((option, index) => {
      const name = text(typeof option === 'string' ? option : option?.name);
      const values = Array.isArray(option?.values) ? option.values : (value.variants || []).map(variant => variant[`option${index + 1}`] ?? variant.options?.[index]);
      const unique = [...new Set(values.map(entry => text(typeof entry === 'object' ? entry?.name : entry)).filter(entry => entry && entry !== 'Default Title'))];
      if (name && unique.length) options.push({ name, values: unique });
    });
    return { id: String(value.id), title: text(value.title), url: url.href, path: url.pathname.replace(/\/$/, ''), image: image?.href || '', imageKey: image?.pathname || '', titleKey: key(value.title), min, max, available: value.available === true, type: text(value.type || value.product_type), options };
  }
  class ProductDiscovery {
    constructor(root) {
      this.root = root;
      try { this.current = normalize(JSON.parse(root.dataset.product)); } catch (_) { return; }
      if (!this.current) return;
      root.dataset.sthDiscoveryReady = 'true';
      this.currency = root.dataset.currency || window.Shopify?.currency?.active || 'USD';
      this.locale = document.documentElement.lang || root.dataset.locale || 'en-US';
      this.selected = new Map();
      this.related = [];
      this.history = [];
      this.generation = 0;
      this.historyGeneration = 0;
      this.controllers = new Set();
      this.status = root.querySelector('[data-sth-discovery-status]');
      this.relatedSection = root.querySelector('[data-sth-related]');
      this.relatedList = root.querySelector('[data-sth-related-list]');
      this.historySection = root.querySelector('[data-sth-history]');
      this.historyList = root.querySelector('[data-sth-history-list]');
      this.toolbar = root.querySelector('[data-sth-compare-toolbar]');
      this.comparison = root.querySelector('[data-sth-comparison]');
      this.fallback = root.querySelector('[data-sth-discovery-fallback]');
      this.retry = root.querySelector('[data-sth-discovery-retry]');
      if (!this.relatedList || !this.historyList || !this.toolbar || !this.comparison) return;
      this.previous = this.readHistory().filter(item => String(item.productId) !== this.current.id);
      this.remember();
      document.addEventListener('sth:browsing-history-change', () => {
        if (!root.isConnected) return;
        this.previous = this.readHistory().filter(item => String(item.productId) !== this.current.id);
        this.historyGeneration++;
        this.loadHistory(this.generation);
      });
      root.addEventListener('change', event => {
        const input = event.target.closest('[data-sth-compare]');
        if (!input) return;
        const product = this.related.find(item => item.id === input.dataset.sthCompare) || this.history.find(item => item.id === input.dataset.sthCompare);
        if (!product || product.id === this.current.id) { input.checked = false; return; }
        if (input.checked && !this.selected.has(product.id) && this.selected.size >= 3) {
          input.checked = false;
          this.announce('Compare up to 3 alternatives at a time. Remove one to add another.');
          return;
        }
        if (input.checked) this.selected.set(product.id, product); else this.selected.delete(product.id);
        this.updateSelection();
      });
      root.querySelector('[data-sth-compare-open]')?.addEventListener('click', event => {
        if (!this.selected.size) return;
        this.opener = event.currentTarget;
        this.renderComparison();
        this.comparison.hidden = false;
        this.comparison.scrollIntoView({ behavior: this.motion(), block: 'start' });
        root.querySelector('[data-sth-compare-close]')?.focus({ preventScroll: true });
      });
      root.querySelector('[data-sth-compare-close]')?.addEventListener('click', () => this.closeComparison());
      this.comparison.addEventListener('keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); this.closeComparison(); }
      });
      root.querySelector('[data-sth-compare-clear]')?.addEventListener('click', () => {
        const first = root.querySelector('[data-sth-compare]:checked');
        this.selected.clear();
        root.querySelectorAll('[data-sth-compare]').forEach(input => { input.checked = false; });
        this.updateSelection();
        first?.focus();
      });
      root.querySelector('[data-sth-history-clear]')?.addEventListener('click', () => {
        let cleared = false;
        try {
          if (window.STHBrowsingHistory?.clear) cleared = window.STHBrowsingHistory.clear();
          else { localStorage.removeItem(historyKey); cleared = true; }
        } catch (_) {}
        if (!cleared) {
          this.announce('Browsing history could not be cleared on this device. Please try again.');
          return;
        }
        this.previous = [];
        this.historyGeneration++;
        this.history = [];
        this.renderHistory();
        this.announce('Your browsing history has been cleared on this device.');
        const focusTarget = !this.relatedSection.hidden ? this.relatedSection.querySelector('h2') : !this.toolbar.hidden ? this.toolbar.querySelector('[data-sth-compare-open]') : this.fallback?.querySelector('a');
        focusTarget?.focus();
      });
      this.retry?.addEventListener('click', () => this.load());
      root.querySelectorAll('[data-sth-scroll]').forEach(button => button.addEventListener('click', () => {
        const rail = button.closest('[data-sth-related],[data-sth-history]')?.querySelector('[data-sth-rail]');
        if (rail) rail.scrollBy({ left: (button.dataset.sthScroll === 'previous' ? -1 : 1) * rail.clientWidth * .85, behavior: this.motion() });
      }));
      root.querySelectorAll('[data-sth-rail]').forEach(rail => {
        rail.addEventListener('scroll', () => this.updateArrows(rail), { passive: true });
        if ('ResizeObserver' in window) {
          const observer = new ResizeObserver(() => this.updateArrows(rail));
          observer.observe(rail);
        }
      });
      this.load();
    }
    motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }
    announce(message) { if (this.status) this.status.textContent = message; }
    money(value) {
      try { return new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency }).format(value / 100); }
      catch (_) { return `${this.currency} ${(value / 100).toFixed(2)}`; }
    }
    price(product) { return product.min === product.max ? this.money(product.min) : `${this.money(product.min)}–${this.money(product.max)}`; }
    readHistory() {
      try {
        if (window.STHBrowsingHistory?.read) return window.STHBrowsingHistory.read();
        const stored = JSON.parse(localStorage.getItem(historyKey) || '[]');
        if (!Array.isArray(stored)) return [];
        const seen = new Set();
        return stored.slice(-24).reverse().filter(item => {
          const id = text(item?.productId), url = productURL(item?.productUrl);
          if (!id || !url || seen.has(id)) return false;
          seen.add(id); return true;
        }).slice(0, 12).reverse();
      } catch (_) { return []; }
    }
    remember() {
      if (window.STHBrowsingHistory?.remember) return window.STHBrowsingHistory.remember({ productId: this.current.id, productUrl: this.current.path });
      const history = [...this.previous, { productId: this.current.id, productUrl: this.current.path }].slice(-12);
      try { localStorage.setItem(historyKey, JSON.stringify(history)); } catch (_) {}
    }
    async fetchJSON(url) {
      const controller = new AbortController();
      this.controllers.add(controller);
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('unavailable');
        return await response.json();
      } finally { clearTimeout(timeout); this.controllers.delete(controller); }
    }
    distinct(candidates, excluded = []) {
      const seenIds = new Set(), seenTitles = new Set(), seenImages = new Set();
      const add = item => { seenIds.add(item.id); seenTitles.add(item.titleKey); if (item.imageKey) seenImages.add(item.imageKey); };
      [this.current, ...excluded].forEach(add);
      return candidates.filter(item => {
        if (!item || seenIds.has(item.id) || seenTitles.has(item.titleKey) || (item.imageKey && seenImages.has(item.imageKey))) return false;
        add(item); return true;
      });
    }
    async load() {
      const generation = ++this.generation;
      this.controllers.forEach(controller => controller.abort());
      this.announce('Finding similar products…');
      if (this.retry) this.retry.hidden = true;
      if (this.fallback) this.fallback.hidden = true;
      let failed = false;
      try {
        const url = new URL(this.root.dataset.recommendationsUrl, location.origin);
        if (url.origin !== location.origin || !url.pathname.endsWith('/recommendations/products')) throw new Error('unavailable');
        url.pathname += '.json';
        url.search = new URLSearchParams({ product_id: this.current.id, limit: '10', intent: 'related' });
        const response = await this.fetchJSON(url.href);
        if (generation !== this.generation || !this.root.isConnected) return;
        this.related = this.distinct((Array.isArray(response.products) ? response.products : []).map(normalize)).slice(0, 8);
      } catch (_) {
        if (generation !== this.generation || !this.root.isConnected) return;
        failed = true;
        this.related = [];
      }
      this.relatedList.replaceChildren(...this.related.map(product => this.card(product, true)));
      this.relatedSection.hidden = !this.related.length;
      this.reconcileSelection();
      if (this.fallback) this.fallback.hidden = this.related.length > 0;
      if (this.retry) this.retry.hidden = !failed;
      this.announce(failed ? 'Product suggestions are unavailable right now. You can try again or keep browsing.' : this.related.length ? `${this.related.length} similar products. Select up to 3 to compare.` : 'Explore more products in this department.');
      this.root.querySelectorAll('[data-sth-rail]').forEach(rail => this.updateArrows(rail));
      await this.loadHistory(generation);
    }
    async loadHistory(generation) {
      const historyGeneration = ++this.historyGeneration;
      const storedIds = new Set(this.previous.map(item => String(item.productId)));
      this.history = this.distinct(this.history.filter(product => storedIds.has(product.id)), this.related);
      this.renderHistory();
      const excluded = new Set([this.current.id, ...this.related.map(product => product.id)]);
      const entries = [...this.previous].reverse().filter(item => !excluded.has(String(item.productId))).slice(0, 6);
      const products = await Promise.all(entries.map(async item => {
        try {
          const url = productURL(item.productUrl); if (!url) return null;
          url.pathname = url.pathname.replace(/\/$/, '') + '.js'; url.search = ''; url.hash = '';
          const product = normalize(await this.fetchJSON(url.href));
          return product?.id === String(item.productId) ? product : null;
        } catch (_) { return null; }
      }));
      if (generation !== this.generation || historyGeneration !== this.historyGeneration || !this.root.isConnected) return;
      this.history = this.distinct(products, this.related);
      this.renderHistory();
    }
    renderHistory() {
      this.historyList.replaceChildren(...this.history.map(product => this.card(product, true)));
      this.historySection.hidden = !this.history.length;
      this.reconcileSelection();
      this.root.querySelectorAll('[data-sth-rail]').forEach(rail => this.updateArrows(rail));
    }
    card(product, compare) {
      const card = node('li', 'sth-discovery-card'); card.dataset.productId = product.id;
      const imageLink = node('a', 'sth-discovery-image'); imageLink.href = product.url; imageLink.setAttribute('aria-label', product.title);
      if (product.image) {
        const image = node('img'); image.src = product.image; image.alt = ''; image.width = 240; image.height = 240; image.loading = 'lazy';
        image.addEventListener('error', () => { image.hidden = true; imageLink.append(node('span', 'sth-discovery-image-missing', 'View product')); }, { once: true });
        imageLink.append(image);
      } else imageLink.append(node('span', 'sth-discovery-image-missing', 'View product'));
      const heading = node('h3', 'sth-discovery-title'), title = node('a', '', product.title); title.href = product.url; heading.append(title);
      card.append(imageLink, heading, node('p', 'sth-discovery-price', this.price(product)));
      if (!product.available) card.append(node('p', 'sth-discovery-availability', 'Currently unavailable'));
      const view = node('a', 'sth-discovery-view', 'View product'); view.href = product.url; card.append(view);
      if (compare) {
        const label = node('label', 'sth-discovery-compare'), input = node('input');
        input.type = 'checkbox'; input.dataset.sthCompare = product.id; input.setAttribute('aria-label', `Compare ${product.title}`);
        input.checked = this.selected.has(product.id);
        label.append(input, node('span', '', 'Compare')); card.append(label);
      }
      return card;
    }
    reconcileSelection() {
      const candidates = new Map([...this.history, ...this.related].map(product => [product.id, product]));
      candidates.delete(this.current.id);
      this.selected.forEach((product, id) => {
        if (candidates.has(id)) this.selected.set(id, candidates.get(id));
        else this.selected.delete(id);
      });
      this.updateSelection(false);
    }
    updateSelection(announce = true) {
      this.root.querySelectorAll('[data-sth-compare]').forEach(input => { input.checked = this.selected.has(input.dataset.sthCompare); });
      const count = this.selected.size;
      this.toolbar.hidden = !count;
      const label = this.root.querySelector('[data-sth-compare-count]'); if (label) label.textContent = `${count} of 3 selected`;
      if (!count) this.comparison.hidden = true;
      else if (!this.comparison.hidden) this.renderComparison();
      if (announce) this.announce(count ? `${count} alternatives selected. You can compare up to 3.` : 'Comparison cleared. Select a product to compare.');
    }
    closeComparison() { this.comparison.hidden = true; this.opener?.focus({ preventScroll: true }); }
    renderComparison() {
      const table = this.root.querySelector('[data-sth-comparison-table]'); if (!table) return;
      const products = [this.current, ...this.selected.values()];
      const caption = node('caption', '', 'Compare product prices and available options');
      const head = node('thead'), headRow = node('tr');
      const corner = node('th', '', 'Product'); corner.scope = 'col'; headRow.append(corner);
      products.forEach((product, index) => {
        const cell = node('th'); cell.scope = 'col'; cell.dataset.productId = product.id;
        if (!index) cell.append(node('span', 'sth-discovery-current', 'This item'));
        if (product.image) { const image = node('img'); image.src = product.image; image.alt = ''; image.width = 120; image.height = 120; image.loading = 'lazy'; cell.append(image); }
        const link = node('a', '', product.title); link.href = product.url; cell.append(link); headRow.append(cell);
      });
      head.append(headRow); const body = node('tbody');
      const row = (label, values) => { const tr = node('tr'), th = node('th', '', label); th.scope = 'row'; tr.append(th, ...values.map(value => node('td', '', value))); body.append(tr); };
      row('Price', products.map(product => this.price(product)));
      row('Availability', products.map(product => product.available ? 'Available' : 'Currently unavailable'));
      if (products.some(product => product.type && key(product.type) !== 'general')) row('Product type', products.map(product => product.type && key(product.type) !== 'general' ? product.type : '—'));
      const names = new Map(); products.forEach(product => product.options.forEach(option => { if (!names.has(key(option.name))) names.set(key(option.name), option.name); }));
      [...names.entries()].slice(0, 6).forEach(([optionKey, name]) => row(name, products.map(product => {
        const values = product.options.find(option => key(option.name) === optionKey)?.values;
        return values?.length ? values.slice(0, 8).join(', ') + (values.length > 8 ? ` + ${values.length - 8} more` : '') : '—';
      })));
      const foot = node('tfoot'), tr = node('tr'), label = node('th', '', 'Details'); label.scope = 'row'; tr.append(label);
      products.forEach(product => { const td = node('td'), link = node('a', 'sth-discovery-view', 'View product'); link.href = product.url; td.append(link); tr.append(td); });
      foot.append(tr); table.replaceChildren(caption, head, body, foot);
    }
    updateArrows(rail) {
      const section = rail.closest('[data-sth-related],[data-sth-history]'); if (!section) return;
      const previous = section.querySelector('[data-sth-scroll="previous"]'), next = section.querySelector('[data-sth-scroll="next"]');
      if (previous) previous.disabled = rail.scrollLeft <= 2;
      if (next) next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
    }
  }
  const initialize = (scope = document) => scope.querySelectorAll('[data-sth-discovery]:not([data-sth-discovery-ready])').forEach(root => new ProductDiscovery(root));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true }); else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
